import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import HeaderTailwind from "../components/generic/HeaderTailwind";
import FooterTailwind from "../components/generic/FooterTailwind";
import participantsService from "../services/participantsService";
import nationsService from "../services/nationsService";
import categoriesService from "../services/categoriesService";
import groupsService from "../services/groupsService";
import resultsService from "../services/resultsService";
import { medalTableService } from "../services/medalTableService";
import { mvpTableService } from "../services/mvpTableService";
import settingsService from "../services/settingsService";

function AdminTailwindDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    participants: 0,
    nations: 0,
    categories: 0,
    groups: 0,
    eventDuration: "0 Tage",
    results: 0,
    medals: { gold: 0, silver: 0, bronze: 0 },
    topMVPScore: 0,
    genderDistribution: { male: 0, female: 0, other: 0 },
    ageGroupDistribution: {},
    medalStandings: [],
    totalMedalsAwarded: 0,
    totalMedalsPossible: 0
  });

  useEffect(() => {
    const loadStatistics = async () => {
      try {
        setLoading(true);
        
        // Lade alle Statistiken parallel
        const [
          participantsResult,
          nationsResult,
          categoriesResult,
          groupsResult,
          resultsResult,
          medalTableResult,
          mvpTableResult,
          settingsResult
        ] = await Promise.all([
          participantsService.getAllParticipants(),
          nationsService.getAllNations(),
          categoriesService.getAllCategories(),
          groupsService.getAllGroups(),
          resultsService.getAllResults(),
          medalTableService.getMedalTable().catch(() => ({ nations: [] })),
          mvpTableService.getMVPTable().catch(() => ({})),
          settingsService.getAllSettings()
        ]);

        // Berechne Statistiken
        const participantsData = participantsResult.success ? participantsResult.data || [] : [];
        const participantsCount = participantsData.length;
        const nationsCount = nationsResult.success ? nationsResult.data.length : 0;
        const categoriesCount = categoriesResult.success ? categoriesResult.data.length : 0;
        const groupsCount = groupsResult.success ? groupsResult.data.length : 0;
        const resultsCount = resultsResult.success ? resultsResult.data.length : 0;

        // Geschlechter- und Altersgruppenverteilung berechnen
        const genderDistribution = { male: 0, female: 0, other: 0 };
        const ageGroupDistribution = {};
        participantsData.forEach((p) => {
          const gender = (p.gender || "").toLowerCase();
          if (gender === "männlich") genderDistribution.male += 1;
          else if (gender === "weiblich") genderDistribution.female += 1;
          else genderDistribution.other += 1;

          const ageGroupName = p.ageGroup?.name || p.ageGroup || "Andere";
          if (!ageGroupDistribution[ageGroupName]) {
            ageGroupDistribution[ageGroupName] = 0;
          }
          ageGroupDistribution[ageGroupName] += 1;
        });

        // Medaillen zählen & Nationen-Ranking
        let medals = { gold: 0, silver: 0, bronze: 0 };
        let medalStandings = [];
        let totalMedalsPossible = 0;
        if (medalTableResult && Array.isArray(medalTableResult.nations)) {
          medalStandings = medalTableResult.nations
            .map((nation) => ({
              name: nation.name,
              code: nation.code,
              gold: nation.gold || 0,
              silver: nation.silver || 0,
              bronze: nation.bronze || 0,
              total: (nation.gold || 0) + (nation.silver || 0) + (nation.bronze || 0)
            }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 4);

          medalTableResult.nations.forEach((nation) => {
            medals.gold += nation.gold || 0;
            medals.silver += nation.silver || 0;
            medals.bronze += nation.bronze || 0;
          });

          if (typeof medalTableResult.totalMedals === "number") {
            totalMedalsPossible = medalTableResult.totalMedals;
          }
        }
        const totalMedalsAwarded = medals.gold + medals.silver + medals.bronze;

        // Höchster MVP-Score finden
        let topMVPScore = 0;
        if (mvpTableResult && typeof mvpTableResult === 'object') {
          Object.values(mvpTableResult).forEach(ageGroupData => {
            if (Array.isArray(ageGroupData) && ageGroupData.length > 0) {
              const maxScore = Math.max(...ageGroupData.map(p => p.totalScore || 0));
              if (maxScore > topMVPScore) {
                topMVPScore = maxScore;
              }
            }
          });
        }

        // Event-Dauer berechnen
        let eventDuration = "0 Tage";
        if (settingsResult.success && settingsResult.data) {
          const startDate = settingsResult.data.competition_start_date?.value;
          const endDate = settingsResult.data.competition_end_date?.value;
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 um Start- und Endtag einzubeziehen
            eventDuration = `${diffDays} ${diffDays === 1 ? 'Tag' : 'Tage'}`;
          }
        }

        setStats({
          participants: participantsCount,
          nations: nationsCount,
          categories: categoriesCount,
          groups: groupsCount,
          eventDuration,
          results: resultsCount,
          medals,
          topMVPScore: topMVPScore.toFixed(2),
          genderDistribution,
          ageGroupDistribution,
          medalStandings,
          totalMedalsAwarded,
          totalMedalsPossible
        });
      } catch (error) {
        console.error('Fehler beim Laden der Statistiken:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, []);

  const completedGroups = Math.min(stats.results, stats.groups || 0);
  const scheduleProgress = stats.groups ? Math.round((completedGroups / stats.groups) * 100) : 0;

  const recentActivities = [
    {
      type: "result",
      iconColor: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
      title: t("admin.dashboard.activity.resultPublished", "Ergebnis veröffentlicht"),
      description: t(
        "admin.dashboard.activity.resultPublishedDesc",
        "Neue Wettkampfergebnisse wurden gespeichert."
      ),
      time: t("admin.dashboard.activity.justNow", "Gerade eben")
    },
    {
      type: "participant",
      iconColor: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
      title: t("admin.dashboard.activity.newParticipant", "Neuer Teilnehmer"),
      description: t(
        "admin.dashboard.activity.newParticipantDesc",
        "Teilnehmerdaten wurden aktualisiert oder hinzugefügt."
      ),
      time: t("admin.dashboard.activity.minutesAgo", "Vor wenigen Minuten")
    },
    {
      type: "schedule",
      iconColor: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
      title: t("admin.dashboard.activity.scheduleUpdated", "Zeitplan aktualisiert"),
      description: t(
        "admin.dashboard.activity.scheduleUpdatedDesc",
        "Gruppen- oder Zeitplaninformationen wurden angepasst."
      ),
      time: t("admin.dashboard.activity.today", "Heute")
    }
  ];

  const getTotalMedalsText = () => {
    if (!stats.totalMedalsAwarded) return "0";
    if (stats.totalMedalsPossible && stats.totalMedalsPossible > 0) {
      return `${stats.totalMedalsAwarded} / ${stats.totalMedalsPossible}`;
    }
    return `${stats.totalMedalsAwarded}`;
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900 transition-colors">
      <HeaderTailwind />
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.dashboard.title", "Übersicht")}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.dashboard.subtitle", "Zentrale Kennzahlen und Status des Turniers.")}
          </p>
            </div>
        </div>

          {/* KPI Cards */}
        {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                      <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                      <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded"></div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Participants */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("admin.dashboard.totalParticipants", "Gesamtteilnehmer")}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {stats.participants}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <i className="fas fa-users text-lg"></i>
                </div>
                    </div>

              {/* Active Areas (Groups) */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("admin.dashboard.activeAreas", "Aktive Gruppen")}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {stats.groups}
                  </p>
                          </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <i className="fas fa-layer-group text-lg"></i>
                            </div>
                            </div>

              {/* Medals Awarded */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("admin.dashboard.medalsAwarded", "Vergebene Medaillen")}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {getTotalMedalsText()}
                  </p>
                        </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                  <i className="fas fa-medal text-lg"></i>
                    </div>
                  </div>

              {/* Nations Represented */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("admin.dashboard.nationsRepresented", "Vertretene Nationen")}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
                    {stats.nations}
                  </p>
                    </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <i className="fas fa-flag text-lg"></i>
                    </div>
                  </div>
                </div>
          )}

          {/* Event Schedule Progress & Quick Actions */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded mb-3"></div>
                <div className="h-3 w-64 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
                <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      <div className="h-3 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
              </div>
            ))}
          </div>
              </div>
              <div className="bg-slate-900 dark:bg-slate-950 rounded-2xl shadow-xl p-6 animate-pulse">
                <div className="h-4 w-28 bg-slate-700 rounded mb-2"></div>
                <div className="h-3 w-40 bg-slate-800 rounded mb-4"></div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-16 rounded-xl bg-slate-800"></div>
                  ))}
                </div>
                <div className="h-10 w-full rounded-xl bg-slate-800"></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Event Schedule Progress */}
              <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("admin.dashboard.scheduleProgress", "Event Schedule Progress")}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t(
                        "admin.dashboard.scheduleProgressSubtitle",
                        "Fortschritt der aktuellen Wettkampfsessions."
                      )}
                    </p>
                  </div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {scheduleProgress}% {t("admin.dashboard.totalProgress", "Gesamtfortschritt")}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-primary dark:bg-primary-light"
                      style={{ width: `${Math.min(scheduleProgress, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Simple timeline-like row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-600 dark:text-slate-300">
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold uppercase tracking-wide text-[11px] text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.morningSession", "Morgensession")}
                    </span>
                    <span className="text-slate-700 dark:text-slate-200">
                      {t("admin.dashboard.sessionCompleted", "Aktuelle Gruppen abgeschlossen.")}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold uppercase tracking-wide text-[11px] text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.afternoonSession", "Nachmittagsession")}
                    </span>
                    <span>
                      {t("admin.dashboard.sessionUpcoming", "Nächste Gruppen in Vorbereitung.")}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold uppercase tracking-wide text-[11px] text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.overallStatus", "Status")}
                    </span>
                    <span>
                      {scheduleProgress >= 100
                        ? t("admin.dashboard.scheduleDone", "Zeitplan abgeschlossen.")
                        : t("admin.dashboard.scheduleRunning", "Zeitplan läuft planmäßig.")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions (dark card) */}
              <div className="bg-slate-900 dark:bg-slate-950 rounded-2xl shadow-xl p-6 flex flex-col justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-white mb-1">
                    {t("admin.dashboard.quickActions", "Quick Actions")}
          </h2>
                  <p className="text-xs text-slate-300 mb-4">
                    {t(
                      "admin.dashboard.quickActionsSubtitle",
                      "Schnelle Zugriffe auf häufig genutzte Funktionen."
                    )}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => navigate("/admin/participants")}
                      className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-100 py-3 transition-colors"
            >
                      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                        <i className="fas fa-user-plus text-xs"></i>
              </div>
                      <span className="text-xs font-medium">
                        {t("admin.dashboard.quickNewEntry", "New Entry")}
                      </span>
                    </button>

                    <button
                      onClick={() => navigate("/admin/results")}
                      className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-100 py-3 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                        <i className="fas fa-file-pdf text-xs"></i>
                </div>
                      <span className="text-xs font-medium">
                        {t("admin.dashboard.quickGeneratePDF", "Generate PDF")}
                      </span>
            </button>

            <button
              onClick={() => navigate("/admin/schedule")}
                      className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-100 py-3 transition-colors"
            >
                      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                        <i className="fas fa-calendar-alt text-xs"></i>
              </div>
                      <span className="text-xs font-medium">
                        {t("admin.dashboard.quickEditSchedule", "Edit Schedule")}
                      </span>
            </button>

            <button
                      onClick={() => navigate("/")}
                      className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-100 py-3 transition-colors"
            >
                      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                        <i className="fas fa-external-link-alt text-xs"></i>
              </div>
                      <span className="text-xs font-medium">
                        {t("admin.dashboard.quickPublicView", "Public View")}
                      </span>
            </button>
          </div>
        </div>

                <button
                  onClick={() => navigate("/admin/schedule-settings")}
                  className="mt-2 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 transition-colors"
                >
                  <i className="fas fa-bolt text-xs"></i>
                  {t("admin.dashboard.systemHealth", "System Health Check")}
                </button>
              </div>
            </div>
          )}

          {/* Middle Row: Demographics & Medal Standings */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Demographics Skeleton */}
              <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                <div className="h-3 w-48 bg-slate-200 dark:bg-slate-700 rounded mb-6"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  <div className="flex items-center justify-center">
                    <div className="w-32 h-32 rounded-full border-4 border-slate-200 dark:border-slate-700"></div>
                  </div>
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded"></div>
                          <div className="h-3 w-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
                        </div>
                        <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Medal Standings Skeleton */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
                <div className="h-3 w-56 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                        <div>
                          <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-1"></div>
                          <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded"></div>
                        </div>
                      </div>
                      <div className="h-3 w-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Demographics */}
              <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("admin.dashboard.demographics", "Demografie")}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.demographicsSubtitle", "Verteilung aller registrierten Teilnehmer.")}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  {/* Gender Donut */}
                  <div className="relative flex items-center justify-center">
                    {(() => {
                      const totalGender =
                        stats.genderDistribution.male +
                        stats.genderDistribution.female +
                        stats.genderDistribution.other || 0;
                      const segments = [
                        { key: "male", color: "#3b82f6", value: stats.genderDistribution.male },
                        { key: "female", color: "#ec4899", value: stats.genderDistribution.female },
                        { key: "other", color: "#64748b", value: stats.genderDistribution.other }
                      ];
                      let offset = 25;
                      return (
                        <>
                          <svg viewBox="0 0 36 36" className="w-40 h-40 -rotate-90">
                            <circle
                              cx="18"
                              cy="18"
                              r="14"
                              fill="none"
                              stroke="#E5E7EB"
                              strokeWidth="4"
                            />
                            {segments.map((seg) => {
                              if (!totalGender || !seg.value) return null;
                              const percent = (seg.value / totalGender) * 100;
                              const dashArray = `${percent} ${100 - percent}`;
                              const circle = (
                                <circle
                                  key={seg.key}
                                  cx="18"
                                  cy="18"
                                  r="14"
                                  fill="none"
                                  stroke={seg.color}
                                  strokeWidth="4"
                                  strokeDasharray={dashArray}
                                  strokeDashoffset={offset}
                                  strokeLinecap="round"
                                />
                              );
                              offset -= percent;
                              return circle;
                            })}
                          </svg>
                          <div className="absolute flex flex-col items-center justify-center pointer-events-none">
                            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                              {t("admin.dashboard.total", "Gesamt")}
                            </div>
                            <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                              {totalGender || 0}
                            </div>
                          </div>
                        </>
                      );
                    })()}

                    {/* Legend */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-10 flex gap-4 text-[11px] text-slate-600 dark:text-slate-400">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span>{t("admin.dashboard.genderMale", "Männlich")}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                        <span>{t("admin.dashboard.genderFemale", "Weiblich")}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        <span>{t("admin.dashboard.genderOther", "Sonstige")}</span>
                      </div>
                    </div>
                  </div>

                  {/* Age Groups Bars */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.ageGroups", "Altersgruppen")}
                    </h3>
                    {Object.entries(stats.ageGroupDistribution).map(([name, value]) => {
                      const percent = stats.participants
                        ? Math.round((value / stats.participants) * 100)
                        : 0;
                      return (
                        <div key={name}>
                          <div className="flex itemscenter justify-between mb-1">
                            <span className="text-xs text-slate-600 dark:text-slate-300">{name}</span>
                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                              {value} ({percent}%)
                            </span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                            <div
                              className="h-2 rounded-full bg-primary dark:bg-primary-light"
                              style={{ width: `${Math.min(percent, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Medal Standings */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t("admin.dashboard.medalStandings", "Medaillenspiegel")}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t("admin.dashboard.medalStandingsSubtitle", "Top-Nationen nach Medaillenanzahl.")}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {stats.medalStandings.map((nation, index) => (
                    <div
                      key={nation.code || nation.name || index}
                      className="flex items-center justify-between py-2 border-b last:border-0 border-slate-100 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {nation.name}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            🥇 {nation.gold} · 🥈 {nation.silver} · 🥉 {nation.bronze}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {nation.total}
                      </div>
                    </div>
                  ))}
                  {stats.medalStandings.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                      {t("admin.dashboard.noMedals", "Noch keine Medaillen vergeben.")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recent Tournament Activity */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
              <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/60 px-4 py-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-40 bg-slate-200 dark:bg-slate-700 rounded"></div>
                        <div className="h-3 w-56 bg-slate-200 dark:bg-slate-700 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 p-6 animate-pulse">
                <div className="h-4 w-32 bg-blue-200/70 rounded mb-3"></div>
                <div className="space-y-2">
                  <div className="h-3 w-40 bg-blue-200/70 rounded"></div>
                  <div className="h-3 w-32 bg-blue-200/70 rounded"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
              <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {t("admin.dashboard.recentActivity", "Recent Tournament Activity")}
                  </h2>
                </div>
                <div className="space-y-3">
                  {recentActivities.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/60 px-4 py-3"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${item.iconColor}`}>
                        {item.type === "result" && <i className="fas fa-flag-checkered"></i>}
                        {item.type === "participant" && <i className="fas fa-user-plus"></i>}
                        {item.type === "schedule" && <i className="fas fa-clock"></i>}
            </div>
            <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {item.title}
                          </p>
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">
                            {item.time}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                </div>

              {/* Info Section rechts klein */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 p-6">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-info-circle text-blue-600 dark:text-blue-400 text-xs"></i>
                </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      {t("admin.dashboard.welcome", "Willkommen im Admin-Dashboard")}
                    </h3>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      {t(
                        "admin.dashboard.welcomeText",
                        "Nutze die Übersicht, um schnell in die wichtigsten Bereiche zu springen."
                      )}
                    </p>
                </div>
              </div>
            </div>
          </div>
          )}
      </div>
    </main>
      <FooterTailwind />
    </div>
  );
}

export default AdminTailwindDashboard;